import { ElevationPoint, type Climb } from '@cya-routes/shared'

interface ClimbSegment {
  startIndex: number
  endIndex: number
  startDistance: number
  endDistance: number
}

export class ClimbDetectionService {
  // Minimum thresholds for climb detection
  private static readonly MIN_CLIMB_GAIN = 70 // meters (focus on significant climbs)
  private static readonly MIN_CLIMB_LENGTH = 1.0 // km (minimum 1km length)
  private static readonly MIN_SUSTAINED_GRADE = 2.5 // percent 
  private static readonly MERGE_GAP_DISTANCE = 1.0 // km (merge related climbs)
  private static readonly MAX_DESCENT_IN_CLIMB = 40 // meters - allow descents within climbs
  private static readonly MAX_FLAT_DISTANCE = 1.0 // km - allow flat sections

  static detectClimbs(elevationProfile: ElevationPoint[]): Climb[] {
    if (!elevationProfile || elevationProfile.length < 2) {
      return []
    }

    console.log('🏔️ Starting climb detection for', elevationProfile.length, 'points')
    
    // 1. Find potential climb segments
    const climbSegments = this.findClimbSegments(elevationProfile)
    console.log('📈 Found', climbSegments.length, 'potential climb segments')
    
    // 2. Merge nearby segments
    const mergedSegments = this.mergeNearbySegments(climbSegments, elevationProfile)
    console.log('🔗 Merged to', mergedSegments.length, 'climb segments')
    
    // 3. Calculate climb metrics and categorize
    const climbs: Climb[] = []
    for (const segment of mergedSegments) {
      const climb = this.analyzeClimb(segment, elevationProfile)
      if (climb && climb.category !== 'Uncategorized') {
        climbs.push(climb)
        console.log(`🚩 ${climb.category} climb: ${climb.length.toFixed(0)}m, ${climb.elevationGain.toFixed(0)}m gain, ${climb.averageGrade.toFixed(1)}% grade`)
      }
    }
    
    console.log('✅ Detected', climbs.length, 'categorized climbs')
    return climbs.sort((a, b) => b.points - a.points)
  }

  private static findClimbSegments(profile: ElevationPoint[]): ClimbSegment[] {
    const segments: ClimbSegment[] = []
    let currentSegment: ClimbSegment | null = null
    let highGradeCount = 0
    let totalGradeChecks = 0
    
    // Track descent/flat section within a climb
    let descentInCurrentClimb = 0
    let flatDistanceInCurrentClimb = 0
    let highestPointInClimb = 0
    
    // Check if distances are in kilometers (likely if max distance < 1000)
    const isKilometers = profile[profile.length - 1].distance < 1000
    console.log(`Distance units: ${isKilometers ? 'kilometers' : 'meters'}`)
    
    for (let i = 0; i < profile.length; i++) {
      const point = profile[i]
      
      // Skip first point if no previous point for comparison
      if (i === 0) continue
      
      const prevPoint = profile[i - 1]
      
      // Calculate grade over a smoothed distance (last 200m or available)
      // Since distances are in km, 0.2km = 200m
      const lookbackDistance = isKilometers ? 0.2 : 200
      const gradeDistance = Math.min(lookbackDistance, point.distance - (profile[Math.max(0, i - 10)]?.distance || 0))
      const grade = this.calculateGradeOverDistance(profile, i, gradeDistance)
      
      totalGradeChecks++
      if (grade >= this.MIN_SUSTAINED_GRADE) {
        highGradeCount++
      }
      
      // Log sample grade calculations for debugging
      if (i === 10 || i === Math.floor(profile.length / 2) || i === profile.length - 10) {
        console.log(`Grade at ${point.distance.toFixed(2)}${isKilometers ? 'km' : 'm'}: ${grade.toFixed(1)}%`)
      }
      
      if (grade >= this.MIN_SUSTAINED_GRADE) {
        if (!currentSegment) {
          // Start new climb segment
          // If we're near the beginning, start from index 0
          const startIndex = i <= 5 ? 0 : Math.max(0, i - 5)
          currentSegment = {
            startIndex: startIndex,
            endIndex: i,
            startDistance: profile[startIndex].distance,
            endDistance: point.distance
          }
          highestPointInClimb = point.elevation
          descentInCurrentClimb = 0
          flatDistanceInCurrentClimb = 0
        } else {
          // Continue climbing - reset descent/flat trackers
          if (point.elevation > highestPointInClimb) {
            highestPointInClimb = point.elevation
            descentInCurrentClimb = 0
          }
          flatDistanceInCurrentClimb = 0
          
          // Extend current segment
          currentSegment.endIndex = i
          currentSegment.endDistance = point.distance
        }
      } else if (currentSegment) {
        // We're in a climb but hit a flat/descent section
        const elevationChange = point.elevation - prevPoint.elevation
        const distanceChange = (point.distance - prevPoint.distance) * (isKilometers ? 1000 : 1) // Convert to meters
        
        if (elevationChange < 0) {
          // Descending
          descentInCurrentClimb += Math.abs(elevationChange)
          flatDistanceInCurrentClimb += distanceChange / 1000 // Track in km
        } else {
          // Flat or slight uphill (but below threshold)
          flatDistanceInCurrentClimb += distanceChange / 1000 // Track in km
        }
        
        // Check if we should end the climb
        if (descentInCurrentClimb > this.MAX_DESCENT_IN_CLIMB || 
            flatDistanceInCurrentClimb > this.MAX_FLAT_DISTANCE) {
          // End current segment if it meets minimum criteria
          const gain = highestPointInClimb - profile[currentSegment.startIndex].elevation
          const lengthRaw = currentSegment.endDistance - currentSegment.startDistance
          const lengthInKm = isKilometers ? lengthRaw : lengthRaw / 1000
          const lengthInMeters = lengthInKm * 1000
          
          console.log(`Potential segment: ${lengthInMeters.toFixed(0)}m long, ${gain.toFixed(0)}m gain (included ${descentInCurrentClimb.toFixed(0)}m descent)`)
          
          if (gain >= this.MIN_CLIMB_GAIN && lengthInKm >= this.MIN_CLIMB_LENGTH) {
            segments.push(currentSegment)
          }
          currentSegment = null
          descentInCurrentClimb = 0
          flatDistanceInCurrentClimb = 0
        } else {
          // Continue the climb despite the flat/descent
          currentSegment.endIndex = i
          currentSegment.endDistance = point.distance
        }
      }
    }
    
    // Don't forget the last segment if we're still climbing at the end
    if (currentSegment) {
      // Look backwards from the end to find the actual peak of this climb
      // This prevents including descents at the end of routes
      let actualEndIndex = currentSegment.endIndex
      let peakElevation = profile[currentSegment.startIndex].elevation
      
      // Find the highest point in this climb segment
      for (let j = currentSegment.startIndex; j <= currentSegment.endIndex; j++) {
        if (profile[j].elevation > peakElevation) {
          peakElevation = profile[j].elevation
          actualEndIndex = j
        }
      }
      
      // Update segment to end at the peak
      currentSegment.endIndex = actualEndIndex
      currentSegment.endDistance = profile[actualEndIndex].distance
      
      const gain = peakElevation - profile[currentSegment.startIndex].elevation
      const lengthRaw = currentSegment.endDistance - currentSegment.startDistance
      const lengthInKm = isKilometers ? lengthRaw : lengthRaw / 1000
      const lengthInMeters = lengthInKm * 1000
      
      console.log(`Final segment (truncated at peak): ${lengthInMeters.toFixed(0)}m long, ${gain.toFixed(0)}m gain`)
      
      if (gain >= this.MIN_CLIMB_GAIN && lengthInKm >= this.MIN_CLIMB_LENGTH) {
        segments.push(currentSegment)
      }
    }
    
    console.log(`📊 Grade analysis: ${highGradeCount}/${totalGradeChecks} points above ${this.MIN_SUSTAINED_GRADE}% (${(highGradeCount/totalGradeChecks*100).toFixed(1)}%)`)
    
    return segments
  }

  private static mergeNearbySegments(segments: ClimbSegment[], profile: ElevationPoint[]): ClimbSegment[] {
    if (segments.length <= 1) return segments
    
    // Check if distances are in kilometers
    const isKilometers = profile[profile.length - 1].distance < 1000
    const mergeGap = isKilometers ? this.MERGE_GAP_DISTANCE : this.MERGE_GAP_DISTANCE * 1000 // Use correct units
    
    const merged: ClimbSegment[] = []
    let current = segments[0]
    
    for (let i = 1; i < segments.length; i++) {
      const next = segments[i]
      const gap = next.startDistance - current.endDistance
      
      // If segments are close together, merge them
      if (gap <= mergeGap) {
        current = {
          startIndex: current.startIndex,
          endIndex: next.endIndex,
          startDistance: current.startDistance,
          endDistance: next.endDistance
        }
      } else {
        merged.push(current)
        current = next
      }
    }
    
    merged.push(current)
    return merged
  }

  private static analyzeClimb(segment: ClimbSegment, profile: ElevationPoint[]): Climb | null {
    const startPoint = profile[segment.startIndex]
    
    // Check if distances are in kilometers
    const isKilometers = profile[profile.length - 1].distance < 1000
    
    // Find the highest point in the climb (to handle descents within climbs)
    let highestElevation = startPoint.elevation
    let totalGain = 0
    let currentElevation = startPoint.elevation
    
    for (let i = segment.startIndex + 1; i <= segment.endIndex; i++) {
      const point = profile[i]
      const elevChange = point.elevation - currentElevation
      if (elevChange > 0) {
        totalGain += elevChange
      }
      if (point.elevation > highestElevation) {
        highestElevation = point.elevation
      }
      currentElevation = point.elevation
    }
    
    const elevationGain = totalGain // Use total positive gain, not just end-start
    const lengthRaw = segment.endDistance - segment.startDistance
    const lengthInKm = isKilometers ? lengthRaw : lengthRaw / 1000
    const lengthInMeters = lengthInKm * 1000
    const averageGrade = lengthInMeters > 0 ? (elevationGain / lengthInMeters) * 100 : 0
    
    // Calculate max grade over any 100m section
    let maxGrade = 0
    const lookbackDistance = isKilometers ? 0.1 : 100 // 0.1km = 100m
    for (let i = segment.startIndex; i <= segment.endIndex; i++) {
      const grade = this.calculateGradeOverDistance(profile, i, lookbackDistance)
      maxGrade = Math.max(maxGrade, grade)
    }
    
    const categoryData = this.categorizeClimb(elevationGain, lengthInMeters, averageGrade, maxGrade)
    
    return {
      startIndex: segment.startIndex,
      endIndex: segment.endIndex,
      startDistance: segment.startDistance,  // Already in km if profile is in km
      endDistance: segment.endDistance,      // Already in km if profile is in km
      elevationGain,
      length: lengthInMeters,
      averageGrade,
      maxGrade,
      category: categoryData.category as any,
      categoryNumber: categoryData.categoryNumber,
      points: categoryData.points,
      color: categoryData.color
    }
  }

  private static categorizeClimb(
    elevationGain: number, 
    length: number, 
    averageGrade: number,
    maxGrade: number
  ): { category: string, categoryNumber: number, points: number, color: string } {
    
    // HC (Hors Catégorie) - Beyond categorization
    if ((averageGrade >= 9 && length >= 8000) || 
        (averageGrade >= 10 && length >= 6000) ||
        (elevationGain >= 1000 && averageGrade >= 7) ||
        (elevationGain >= 800 && averageGrade >= 8) ||
        (elevationGain >= 600 && averageGrade >= 10)) {
      return { category: 'HC', categoryNumber: 0, points: 20, color: '#991B1B' } // Dark red-800
    }
    
    // Category 1 - Very hard
    if ((averageGrade >= 7.5 && length >= 5000) ||
        (averageGrade >= 8 && length >= 3000) ||
        (elevationGain >= 500 && averageGrade >= 6) ||
        (elevationGain >= 400 && averageGrade >= 7) ||
        (elevationGain >= 300 && averageGrade >= 9)) {
      return { category: 'Cat 1', categoryNumber: 1, points: 10, color: '#DC2626' } // Red-600
    }
    
    // Category 2 - Hard
    if ((averageGrade >= 5.5 && length >= 3000) ||
        (averageGrade >= 7 && length >= 2000) ||
        (elevationGain >= 250 && averageGrade >= 5) ||
        (elevationGain >= 200 && averageGrade >= 6) ||
        (elevationGain >= 150 && averageGrade >= 8)) {
      return { category: 'Cat 2', categoryNumber: 2, points: 5, color: '#F97316' } // Orange-500
    }
    
    // Category 3 - Moderate
    if ((averageGrade >= 4 && length >= 2000) ||
        (averageGrade >= 5 && length >= 1500) ||
        (elevationGain >= 150 && averageGrade >= 4) ||
        (elevationGain >= 100 && averageGrade >= 6)) {
      return { category: 'Cat 3', categoryNumber: 3, points: 2, color: '#EAB308' } // Yellow-600
    }
    
    // Category 4 - Easy
    if ((averageGrade >= 3 && length >= 1000) ||
        (averageGrade >= 4 && length >= 500) ||
        (elevationGain >= 80 && averageGrade >= 3.5) ||
        (elevationGain >= 50 && averageGrade >= 5)) {
      return { category: 'Cat 4', categoryNumber: 4, points: 1, color: '#16A34A' } // Green-600
    }
    
    return { category: 'Uncategorized', categoryNumber: 5, points: 0, color: '#6B7280' } // Gray
  }

  private static calculateGradeOverDistance(
    profile: ElevationPoint[], 
    endIndex: number, 
    distance: number
  ): number {
    if (endIndex === 0) return 0
    
    const endPoint = profile[endIndex]
    
    // Check if distances are in meters or kilometers
    const isKilometers = profile[profile.length - 1].distance < 1000 // If total distance < 1000, it's likely km
    
    // distance parameter is the lookback distance in the units of the profile
    const targetStartDistance = Math.max(0, endPoint.distance - distance)
    
    // Find the point closest to targetStartDistance
    let startIndex = endIndex - 1
    while (startIndex > 0 && profile[startIndex].distance > targetStartDistance) {
      startIndex--
    }
    
    const startPoint = profile[startIndex]
    const elevationChange = endPoint.elevation - startPoint.elevation
    const actualDistanceRaw = endPoint.distance - startPoint.distance
    const actualDistance = isKilometers ? actualDistanceRaw * 1000 : actualDistanceRaw // Convert to meters for grade calc
    
    return actualDistance > 0 ? Math.max(0, (elevationChange / actualDistance) * 100) : 0
  }
}